import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import * as PaymentActions from './payment.actions';
import { createSelector, Store } from '@ngrx/store';
import { PaymentRepository } from '@core/repositories/payment.repository';
import { catchError, debounceTime, distinctUntilChanged, map, of, startWith, switchMap, take, tap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ClearAsyncErrorAction, SetAsyncErrorAction, SetValueAction, StartAsyncValidationAction } from 'ngrx-forms';
import { PAYMENT_CHARGE_FORM_ID } from './payment-charge.form';
import { DOCUMENT } from '@angular/common';
import { PAYMENT_WITHDRAW_FORM_ID } from './payment-withdraw.form';
import { PAYMENT_WITHDRAW_ADDRESS_ASYNC_ERROR_KEY } from '@data/const';
import { NotificationService } from '@app2/core/services/notifications/notification.service';
import { userActions } from '@store/user';
import { ErrorResponse, TransactionList } from '@core/models';
import { paymentSelectors } from './payment.selectors';
import { settingsSelectors } from '@store/settings';
import { paymentActions } from './payment.actions';
import { PaymentModes } from '@store/payment/payment.reducer';
import { routerSelectors } from '@store/router';
import { AnalyticsEventsEnum } from '@core/models/analytics.model';
import { AnalystService } from '@app/services/analyst/analyst.service';


const selectChargeInitialComplexData = createSelector(
  paymentSelectors.selectCurrencyList,
  paymentSelectors.selectChargePaymentTypeList,
  settingsSelectors.selectUserCurrency,
  (currencyList, paymentTypeList, currencyIso) =>
    ([ currencyList, paymentTypeList, currencyIso ] as const),
);

const selectWithdrawInitialComplexData = createSelector(
  paymentSelectors.selectWithdrawPaymentTypeList,
  settingsSelectors.selectUserCurrency,
  (paymentTypeList, currencyIso) =>
    ([ paymentTypeList, currencyIso ] as const),
);


@Injectable()
export class PaymentEffects {

  constructor(
    private actions: Actions,
    private store: Store,
    private notification: NotificationService,
    private paymentRepository: PaymentRepository,
    private analystService: AnalystService,
    @Inject(DOCUMENT) private document: Document,
  ) {
  }

  public initCurrencyList = createEffect(() => {
    return this.actions.pipe(
      ofType(
        PaymentActions.initChargePayment,
        PaymentActions.initWithdrawPayment,
      ),
      map(() => PaymentActions.loadCurrencyList()),
    );
  });

  public initPaymentTypeList = createEffect(() => {
    return this.actions.pipe(
      ofType(
        PaymentActions.initChargePayment,
        PaymentActions.initWithdrawPayment,
      ),
      map(() => PaymentActions.loadPaymentTypeList()),
    );
  });

  public setWithdrawInitialFormValues = createEffect(() => {
    return this.actions.pipe(
      ofType(PaymentActions.initWithdrawPayment),
      switchMap(() => this.store.select(selectWithdrawInitialComplexData)
        .pipe(
          filter(([paymentTypeList]) => paymentTypeList.length > 0),
          take(1),
          map(([ paymentTypeList, currencyIso ]) => {
            const paymentTypeSupportsThisCurrency = paymentTypeList
              .find(type => type.iso_codes.find(isoCode => isoCode === currencyIso));
            return new SetValueAction(
              `${ PAYMENT_WITHDRAW_FORM_ID }.paymentType`,
              paymentTypeSupportsThisCurrency ? paymentTypeSupportsThisCurrency.type : paymentTypeList[0],
            );
          }),
        ),
      ),
    );
  });

  public setChargeInitialFormValues = createEffect(() => {
    return this.actions.pipe(
      ofType(PaymentActions.initChargePayment),
      switchMap(() => this.store.select(selectChargeInitialComplexData)
        .pipe(
          filter(([ currencyList, paymentTypeList, currencyIso ]) => {
            return !!currencyList.length && !!paymentTypeList.length && !!currencyIso;
          }),
          take(1),
          map(([ currencyList, , currencyIso ]) =>
            new SetValueAction(
              `${ PAYMENT_CHARGE_FORM_ID }.currency`,
              currencyList.some(currency => currency.iso_code === currencyIso)
                ? currencyIso
                : currencyList[0] ?? '',
            ),
          ),
        )),
    );
  });


  public getCurrencyList = createEffect(() => {
    return this.actions.pipe(
      ofType(PaymentActions.loadCurrencyList),
      concatLatestFrom(() => this.store.select(paymentSelectors.selectCurrencyList)),
      filter(([, currencyList ]) => !currencyList.length),
      switchMap(() => this.paymentRepository.getCurrencyList()
        .pipe(
          map(({ currencies }) => PaymentActions.loadCurrencyListSuccess({ currencies })),
          catchError((error: unknown) => of(PaymentActions.loadCurrencyListFailure({ error }))),
        ),
      ),
    );
  });

  public getPaymentTypeList = createEffect(() => {
    return this.actions.pipe(
      ofType(PaymentActions.loadPaymentTypeList),
      concatLatestFrom(() => [
        this.store.select(paymentSelectors.selectChargePaymentTypeList),
        this.store.select(paymentSelectors.selectWithdrawPaymentTypeList),
      ]),
      filter(([, chargePaymentTypeList, withdrawPaymentTypeList ]) => {
        return ![ ...chargePaymentTypeList, ...withdrawPaymentTypeList ].length;
      }),
      switchMap(() => this.paymentRepository.getPaymentTypesList()
        .pipe(
          map((paymentTypes) => PaymentActions.loadPaymentTypeListSuccess(paymentTypes)),
          catchError((error: unknown) => of(PaymentActions.loadPaymentTypeListFailure({ error }))),
        ),
      ),
    );
  });

  public updateChargeAmount = createEffect(() => {
    return this.actions.pipe(
      ofType(PaymentActions.updateChargeAmount),
      map(({ value }) => new SetValueAction(`${ PAYMENT_CHARGE_FORM_ID }.amount`, value)),
    );
  });

  public sendChargePaymentRequest = createEffect(() => {
    return this.actions.pipe(
      ofType(PaymentActions.sendChargePaymentRequest),
      concatLatestFrom(() => [
        this.store.select(paymentSelectors.selectChargeForm),
        this.store.select(paymentSelectors.selectResultChargeAmount),
      ]),
      filter(([,
        {
          isValid,
          value: { paymentType },
        },
        amount,
      ]) => isValid && !!paymentType && !!amount),
      switchMap((([, { value: { paymentType } }, amount]) =>
        this.paymentRepository.postPaymentCharge(paymentType!, amount).pipe(
          map((invoice) => PaymentActions.sendChargePaymentRequestSuccess({ invoice })),
          catchError((error: unknown) => of(PaymentActions.sendChargePaymentRequestFailure({ error }))),
        )),
      ),
    );
  });

  // public clearChargeForm = createEffect(() => {
  //   return this.actions.pipe(
  //     ofType(
  //       PaymentActions.sendChargePaymentRequestSuccess,
  //       PaymentActions.sendChargePaymentRequestFailure,
  //     ),
  //     map(() => PaymentActions.clearChargeForm()),
  //   );
  // });

  public openChargePaymentLink = createEffect(() => {
    return this.actions.pipe(
      ofType(PaymentActions.sendChargePaymentRequestSuccess),
      tap(({ invoice }) => {
        if (invoice.field === PaymentModes.InvoiceLink) {
          this.analystService.logEvent(AnalyticsEventsEnum.UpBalanceApplyTap).then();
          this.document.defaultView?.open(invoice.invoice_link, '_blank');
        }
        if (!!invoice.volet_params) {
          const form = this.document.createElement('form');
          form.method = 'POST';
          form.action = (invoice.volet_params as any).form_redirect_url;

          Object.entries(invoice.volet_params).forEach(([key, value]) => {
            const input = this.document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = value;
            form.appendChild(input);
          });

          this.document.body.appendChild(form);
          form.target = '_blank';
          form.submit();
        }
        //
      }),
    );
  }, { dispatch: false } );

  public updateWithdrawAddress = createEffect(() => {
    return this.actions.pipe(
      ofType(PaymentActions.updateWithdrawAddress),
      map(({ value }) => new SetValueAction(`${ PAYMENT_WITHDRAW_FORM_ID }.address`, value)),
    );
  });

  public clearWithdrawAddressAsyncError = createEffect(() => {
    return this.actions.pipe(
      ofType(PaymentActions.clearWithdrawAddressAsyncError),
      concatLatestFrom(() => this.store.select(paymentSelectors.selectHasWithdrawAddressAsyncError)),
      filter(([, hasError ]) => hasError),
      map(() => new ClearAsyncErrorAction(`${ PAYMENT_WITHDRAW_FORM_ID }.address`, PAYMENT_WITHDRAW_ADDRESS_ASYNC_ERROR_KEY)),
    );
  });

  public validateWithdrawAddress = createEffect(() => {
    return this.actions.pipe(
      ofType(SetValueAction.TYPE),
      concatLatestFrom(() => this.store.select(paymentSelectors.selectWithdrawAddressIsCreditCard)),
      filter(([ { controlId, value } ]) => {
        return <string>controlId === `${ PAYMENT_WITHDRAW_FORM_ID }.address` && !!<string>value;
      }),
      distinctUntilChanged(([ { value: prev }], [ { value: curr }]) => prev === curr),
      switchMap(([ { controlId, value }, isCreditCard ]) => {
        return (
          isCreditCard
            ? this.paymentRepository.checkCard(<string>value)
            : this.paymentRepository.checkCryptoWallet(<string>value)
        )
          .pipe(
            map(() => new ClearAsyncErrorAction(<string>controlId, PAYMENT_WITHDRAW_ADDRESS_ASYNC_ERROR_KEY)),
            catchError(() => {
              return of(new SetAsyncErrorAction(<string>controlId, PAYMENT_WITHDRAW_ADDRESS_ASYNC_ERROR_KEY, true));
            }),
            startWith(new StartAsyncValidationAction(<string>controlId, PAYMENT_WITHDRAW_ADDRESS_ASYNC_ERROR_KEY)),
          );
      }),
    );
  });

  public sendWithdrawPaymentRequest = createEffect(() => {
    return this.actions.pipe(
      ofType(PaymentActions.sendWithdrawPaymentRequest),
      concatLatestFrom(() => this.store.select(paymentSelectors.selectWithdrawForm)),
      filter(([, {
        isValid,
        value: { currency, amount, paymentType, address },
      }]) => isValid && !!currency && !!amount && !!paymentType && !!address),
      switchMap((([, { value: { currency, amount, paymentType, address } }]) =>
        this.paymentRepository.postPaymentWithdraw(paymentType!, amount, address!, currency!).pipe(
          map(() => PaymentActions.sendWithdrawPaymentRequestSuccess()),
          catchError((error: unknown) => of(PaymentActions.sendWithdrawPaymentRequestFailure({ error }))),
        ))),
    );
  });

  public withdrawPaymentSuccess = createEffect(() => {
    return this.actions.pipe(
      ofType(PaymentActions.sendWithdrawPaymentRequestSuccess),
      map(() => userActions.getUser()),
      tap(() => {
        this.notification.success('Успешный вывод средств');
      }),
    );
  });

  public onPaymentFailure = createEffect(() => {
    return this.actions.pipe(
      ofType(
        PaymentActions.sendWithdrawPaymentRequestFailure,
        PaymentActions.sendChargePaymentRequestFailure,
      ),
      debounceTime(10),
      tap(({ error }) => {
        this.notification.error(error.error.errors[0].error_message);
      }),
    );
  }, { dispatch: false });

  public clearWithdrawForm = createEffect(() => {
    return this.actions.pipe(
      ofType(
        PaymentActions.sendWithdrawPaymentRequestSuccess,
        PaymentActions.sendWithdrawPaymentRequestFailure,
      ),
      map(() => PaymentActions.clearWithdrawForm()),
    );
  });


  public loadTransactions = createEffect(() => {
    return this.actions.pipe(
      ofType(PaymentActions.loadTransactions),
      switchMap(() => this.paymentRepository.getPaymentTransactions()
        .pipe(
          map(( { transactions, next_page: nextPage }: TransactionList ) =>
            PaymentActions.loadTransactionsSuccess({ transactions, nextPage }),
          ),
          catchError((error: unknown) => of(PaymentActions.loadTransactionsFailure({ error }))),
        ),
      ),
    );
  });


  public loadTransactionsNextPage = createEffect(() => {
    return this.actions.pipe(
      ofType(PaymentActions.loadTransactionsNextPage),
      concatLatestFrom(() => this.store.select(paymentSelectors.selectTransactionsNextPage)),
      switchMap(([, page]) => this.paymentRepository.getPaymentTransactions(page)
        .pipe(
          map(( { transactions, next_page: nextPage }: TransactionList ) =>
            PaymentActions.loadTransactionsNextPageSuccess({ transactions, nextPage }),
          ),
          catchError((error: unknown) => of(PaymentActions.loadTransactionsNextPageFailure({ error: error as ErrorResponse }))),
        ),
      ),
    );
  });

  public getPaymentStatus = createEffect(() => {
    return this.actions.pipe(
      ofType(paymentActions.getPaymentStatus),
      concatLatestFrom(() => this.store.select(routerSelectors.selectRouterParams)),
      switchMap(([, params]) => this.paymentRepository.paymentStatus(params['paymentId'])
        .pipe(
          map((  response ) => paymentActions.getPaymentStatusSuccess({ status: response.data })),
          catchError((error: unknown) => of(paymentActions.getPaymentStatusFailure({ error: error as ErrorResponse }))),
        ),
      ),
    );
  });
}
